import { Box } from '@modusign/design-system';
import { useDropdownField } from '@modusign/react-signing';
import { DropdownOption } from '@modusign/react-signing/types';
import { cx } from 'emotion';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { useToggle } from 'react-use';

import RoundedXButton from '@app/components/Buttons/RoundedXButton';
import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import {
  toAbsolutePoint,
  toAbsoluteSize,
  toRelativePoint,
  toRelativeSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/libs/absRelConverter';
import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { createAbsoluteSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/createAbsoluteSize';
import { getSignerDisplayName } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getSignerDisplayName';
import { getTextBaseFieldMinimumSize } from '@app/features/apply-react-signing/components/FieldRenderer/libs/getTextBaseFieldMinimumSize';
import {
  AbsolutePoint,
  ReactSigningFieldRendererProps,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import DropdownFieldContextMenu from '@app/features/apply-react-signing/components/FieldSetupStep/DropdownFieldContextMenu';
import DropdownFieldDropdownPreview from '@app/features/apply-react-signing/components/FieldSetupStep/DropdownFieldPreview';
import { useOptimalFieldsContext } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';
import { useTransformTextStyle } from '@app/features/apply-react-signing/libs/hooks/useTransFormTextStyle';
import {
  MAX_OPTION_COUNT,
  validateDropdownFieldOptions,
} from '@app/features/apply-react-signing/libs/validations/validateDropdownFieldOptions';
import iOS from '@app/libs/isiOS';
import { isSafari } from '@app/libs/isSafari';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';
import PDFFontNormalizer from '@app/libs/reactPdfJs/utils/PDFFontNormalizer';

import './ConfigurableDropDownField.scss';

export const DROPDOWN_PLACE_HOLDER = '옵션 선택';

function getOptionsValidationMessage(options: DropdownOption[]) {
  const error = validateDropdownFieldOptions(options);

  if (error === 'NO_OPTION') {
    return '입력한 옵션이 없습니다.';
  }

  if (error === 'TOO_MANY_OPTION') {
    return `최대 ${MAX_OPTION_COUNT}개까지만 적용됩니다.`;
  }

  if (error === 'DUPLICATED_OPTION') {
    return '동일한 옵션이 있습니다.';
  }

  return '';
}

export function ConfigurableDropdownFieldRenderer({
  restrictModification,
  readOnly,
  fieldId,
  isPreview,
  scale,
  viewport,
  isDataLabelEditable,
}: ReactSigningFieldRendererProps) {
  const [showContextMenu, toggleContextMenu] = useToggle(true);
  const [isPreviewButtonHover, setIsPreviewButtonHover] = useState(false);
  const [showGuideline, setShowGuideline] = useState(false);
  const {
    field,
    setPosition,
    setSize,
    setDataLabel,
    setDefaultInputValue,
    setFontSize,
    setFontStyle,
    setRequired,
    setOptions,
  } = useDropdownField(fieldId);

  const fontPixelSize: number = PDFFontNormalizer.toPixelFontSize(
    { fontSize: field?.config.fontStyle.size ?? 60 },
    scale
  );

  const {
    evaluated: { isDataLabelDuplicated },
    removeField,
  } = useOptimalFieldsContext<'DROPDOWN', 'SIGNER'>();

  const absoluteSize = toAbsoluteSize(field?.size ?? { width: 0, height: 0 }, viewport);
  const absolutePoint = toAbsolutePoint(field?.position ?? { x: 0, y: 0 }, viewport);
  const [showInvalidStatus, setShowInvalidStatus] = useState<boolean>(
    (field?.config.options.length ?? -1) > 0
  );
  const observer = useRef<ResizeObserver | null>();

  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (selectRef.current) {
      const { width, height } = selectRef.current.getBoundingClientRect();
      const relativeSize = toRelativeSize(
        createAbsoluteSize({ width: width - fontPixelSize, height }),
        viewport
      );
      setSize(relativeSize.width, relativeSize.height);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectRef.current?.getBoundingClientRect()]);

  useEffect(() => {
    return () => {
      observer.current?.disconnect();
      observer.current = null;
    };
  }, []);

  const fontSize = field?.config.fontStyle.size ?? 12;
  const { transformTextStyle } = useTransformTextStyle({
    fontFamily: field?.config.fontStyle.type ?? 'NOTO_SANS',
    textAlign: field?.config.textAlign ?? 'LEFT',
    fontSize,
    size: createAbsoluteSize({
      width: absoluteSize.width + fontPixelSize ?? 0,
      height: absoluteSize.height,
    }),
    scale,
  });

  const optionsValidationMessage = getOptionsValidationMessage(
    field?.config.options as DropdownOption[]
  );

  // 'g', 'j', 'q' 등과 같이 폰트 높이 아래로 튀어 나오는 영역을 보정하기 위한 값
  const minSize = getTextBaseFieldMinimumSize(fontSize, scale);

  const dataLabelErrorMessage = useMemo(() => {
    if (!field?.dataLabel.trim()) {
      return '데이터 라벨 입력은 필수입니다.';
    }

    if (isDataLabelDuplicated) {
      return '중복된 데이터 라벨입니다.';
    }

    return '';
  }, [field?.dataLabel, isDataLabelDuplicated]);

  /**
   * width, height를 fontSize & maxOptionLength 통해 보정"(correct) 해주는 함수.
   */

  const handleChangeFontSize = useCallback(
    (fontSize: number) => {
      setFontSize(fontSize);
    },
    [setFontSize]
  );

  const handleRemoveField = useCallback(() => {
    removeField(fieldId);
  }, [fieldId, removeField]);

  const handlePointChangeStart = useCallback(() => {
    setShowGuideline(true);
    if (!isPreviewButtonHover) {
      toggleContextMenu(true);
    }
  }, [isPreviewButtonHover, toggleContextMenu]);

  const handlePointChange = useCallback(
    (point: AbsolutePoint) => {
      const relativePoint = clampFieldPoint(toRelativePoint(point, viewport));
      setPosition(relativePoint.x, relativePoint.y);
      setShowGuideline(true);
    },
    [setPosition, viewport]
  );

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    // 0번은 옵션 리스트에 출력되진 않지만 라벨로 보여주는 옵션을 의미하고,
    // 1은 가장 첫번째로 선택하는 --선택-- 같은 옵션으로 클릭 시 0번으로 돌아가는걸 기대합니다.
    // 따라서, 0, 1번을 선택한 경우 초기상태로 간주하여 값을 설정합니다.
    // iOS에서는 select option에 대한 display none 속성이 적용되지 않아 플레이스홀더와 기본값이 보이는 이슈가 존재합니다.[1]
    // 이를 회피하기 위한 의사결정[2]에 따라 분기를 구성합니다.
    // [1] https://bugs.webkit.org/show_bug.cgi?id=8351
    // [2] https://modusign.slack.com/archives/C03Q72ENPQE/p1701244382043339?thread_ts=1701220511.471019&cid=C03Q72ENPQE
    const uiReservedCount = isSafari() || iOS ? 0 : 1;
    if (e.target.selectedIndex <= uiReservedCount) {
      setDefaultInputValue(null);
    } else {
      setDefaultInputValue(e.target.value);
    }
  };
  if (!field || field.participantReference.type !== 'SIGNER') {
    return null;
  }

  const handlePointChangeStop = () => {
    setShowGuideline(false);
  };

  const handleOptionsChange = (options: DropdownOption[]) => {
    if (!options.some((o) => o.value === field.config.defaultInputValue)) {
      setDefaultInputValue(null);
    }
    setOptions(options);
  };

  const isFieldWidthExceedPageWidth = field.size.width > 1;
  const participantOrder = field.participantReference.index + 1;

  const configurableDropdownFieldClassByOrder = createFieldClassNameByParticipantOrder(
    'ConfigurableDropdownField',
    participantOrder
  );

  return (
    <FieldBase
      className={cx({
        ConfigurableDropdownField: true,
        [configurableDropdownFieldClassByOrder]: true,
      })}
      data-testid="dropdown-field"
      dragCancelSelectors={['.ConfigurableDropdownField__context-menu']}
      fieldGuideline={{
        guideLineOffset: {
          left: 2,
          top: 2,
          right: 1 - fontPixelSize,
          bottom: 1,
        },
        viewport,
        showGuideLine: showGuideline,
      }}
      maxConstraints={[viewport.width - absolutePoint.x, viewport.height - absolutePoint.y]}
      minConstraints={[minSize.width, minSize.height]}
      point={absolutePoint}
      readOnly={readOnly}
      resizeHandles={[]}
      restrictModification={restrictModification}
      size={createAbsoluteSize({
        width: absoluteSize.width + fontPixelSize,
        height: absoluteSize.height,
      })}
      snapSize={createAbsoluteSize({
        width: 1,
        height: fontPixelSize,
      })}
      onPointChange={handlePointChange}
      onPointChangeStart={handlePointChangeStart}
      onPointChangeStop={handlePointChangeStop}
    >
      <Box height="100%" width="100%">
        <OutsideClickHandler
          onOutsideClick={() => {
            if (field.dataLabel && !isDataLabelDuplicated && !optionsValidationMessage) {
              toggleContextMenu(false);
            }

            if (!showInvalidStatus) {
              setShowInvalidStatus(true);
            }
          }}
        >
          {showContextMenu && !readOnly && !restrictModification && (
            <Box className="ConfigurableDropdownField__context-menu">
              <DropdownFieldContextMenu
                dataLabel={field.dataLabel}
                dataLabelErrorMessage={dataLabelErrorMessage}
                delimiter=";"
                fontFamily={field.config.fontStyle.type}
                fontSize={field.config.fontStyle.size}
                isDataLabelEditable={isDataLabelEditable}
                isDirty={showInvalidStatus}
                isRequired={field.config.required}
                options={field.config.options}
                optionsValidationMessage={optionsValidationMessage}
                participantName={getSignerDisplayName(field.participantReference)}
                participantOrder={participantOrder}
                onDataLabelChange={(dataLabel) => setDataLabel(dataLabel)}
                onDirty={setShowInvalidStatus}
                onFontFamilyChange={(fontFamily) => setFontStyle(fontFamily)}
                onFontSizeChange={handleChangeFontSize}
                onOptionsChange={handleOptionsChange}
                onRequiredToggle={(_, required) => setRequired(required)}
              />
            </Box>
          )}
          <Box height="100%" position="absolute" width="100%">
            <DropdownFieldDropdownPreview
              fontPixelSize={fontPixelSize}
              invalid={
                (!!optionsValidationMessage ||
                  !!dataLabelErrorMessage ||
                  isFieldWidthExceedPageWidth) &&
                showInvalidStatus
              }
              options={field.config.options}
              participantOrder={participantOrder}
              restrictTextClick={!readOnly && !isPreview}
              selectRef={selectRef}
              star={field.config.required}
              style={transformTextStyle}
              value={field?.config.defaultInputValue ?? ''}
              onHoveredOnPreviewButton={setIsPreviewButtonHover}
              onSelectChange={handleSelectChange}
            />
            {!readOnly && !restrictModification && (
              <Box className="ConfigurableDropdownField__x-button">
                <RoundedXButton onClick={handleRemoveField} />
              </Box>
            )}
          </Box>
        </OutsideClickHandler>
      </Box>
    </FieldBase>
  );
}
